import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Pencil, Trash2, Eye } from "lucide-react";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { startOfDay, endOfDay, subDays } from "date-fns";
import { useRef } from "react";
import TenantDetails from "@/pages/TenantDetails";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { SubscriptionTransactionFilters, SubscriptionTransactionFilterState } from "@/components/subscriptions/SubscriptionTransactionFilters";
import { toast } from "sonner";
import { logCrud } from "@/services/activities";
import { EditSubscriptionTransactionModal } from "@/components/admin/EditSubscriptionTransactionModal";
import { TenantDetailsModal } from "@/components/admin/TenantDetailsModal";

const SubscriptionTransactions = () => {
  const navigate = useNavigate();
  const { isAdminSaas } = useTenant();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<SubscriptionTransactionFilterState>({
    searchTerm: "",
    statuses: [],
    transactionTypes: [],
    paymentTypes: [],
    paymentMethods: [],
    tenantIds: [],
    dateFrom: "",
    dateTo: "",
    expFrom: "",
    expTo: "",
    amountMin: "",
    amountMax: "",
    hasCoupon: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editOpen, setEditOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<any | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [tenantProfile, setTenantProfile] = useState<{ id: string; name: string } | null>(null);
  const [rxFrom, setRxFrom] = useState<Date | null>(null);
  const [rxTo, setRxTo] = useState<Date | null>(null);

  const { data: tenants = [] } = useQuery({
    queryKey: ["tenants-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("id, name")
        .order("name", { ascending: true }) as any;
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const tenantOptions = useMemo(() => tenants.map((t: any) => ({ id: t.id, label: t.name })), [tenants]);

  const { data: transactions, isLoading } = useQuery({
    queryKey: ["subscription-transactions-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("*")
        .order("payment_date", { ascending: false, nullsFirst: false })
        .order("expiration_date", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const getTenantName = (tenantId: string) => {
    const t = tenantOptions.find((x) => x.id === tenantId);
    return t?.label || tenantId;
  };

  const matchesFilters = (tx: any) => {
    if (filters.searchTerm) {
      const s = filters.searchTerm.toLowerCase();
      const name = getTenantName(tx.tenant_id).toLowerCase();
      const coupon = (tx.metadata?.coupon_code || "").toLowerCase();
      const ok = name.includes(s) || (tx.payment_method || "").toLowerCase().includes(s) || coupon.includes(s);
      if (!ok) return false;
    }
    if (filters.tenantIds.length > 0 && !filters.tenantIds.includes(tx.tenant_id)) return false;
    if (filters.statuses.length > 0 && !filters.statuses.includes(tx.status)) return false;
    if (filters.transactionTypes.length > 0 && !filters.transactionTypes.includes(tx.transaction_type)) return false;
    if (filters.paymentTypes.length > 0 && !filters.paymentTypes.includes(tx.payment_type || "regular")) return false;
    if (filters.paymentMethods.length > 0 && !filters.paymentMethods.includes(tx.payment_method || "-")) return false;
    if (filters.hasCoupon !== null) {
      const has = Boolean(tx.metadata?.coupon_code);
      if (filters.hasCoupon !== has) return false;
    }
    if (filters.dateFrom) {
      const pay = tx.payment_date ? new Date(tx.payment_date) : null;
      if (!pay || pay < new Date(filters.dateFrom)) return false;
    }
    if (filters.dateTo) {
      const pay = tx.payment_date ? new Date(tx.payment_date) : null;
      if (!pay || pay > new Date(filters.dateTo)) return false;
    }
    if (filters.expFrom) {
      const exp = tx.expiration_date ? new Date(tx.expiration_date) : null;
      if (!exp || exp < new Date(filters.expFrom)) return false;
    }
    if (filters.expTo) {
      const exp = tx.expiration_date ? new Date(tx.expiration_date) : null;
      if (!exp || exp > new Date(filters.expTo)) return false;
    }
    if (filters.amountMin) {
      const min = Number(filters.amountMin);
      if (!(Number(tx.amount || 0) >= min)) return false;
    }
    if (filters.amountMax) {
      const max = Number(filters.amountMax);
      if (!(Number(tx.amount || 0) <= max)) return false;
    }
    return true;
  };

  const filteredTransactions = useMemo(() => {
    if (!transactions) return [] as any[];
    return transactions.filter((tx: any) => matchesFilters(tx));
  }, [transactions, filters, tenantOptions]);

  const facetCounts = useMemo(() => {
    const counts = {
      statuses: {} as Record<string, number>,
      transactionTypes: {} as Record<string, number>,
      paymentTypes: {} as Record<string, number>,
      paymentMethods: {} as Record<string, number>,
      tenants: {} as Record<string, number>,
      coupons: { com: 0, sem: 0 },
    };
    if (!transactions) return counts;
    transactions.forEach((tx: any) => {
      if (!matchesFilters(tx)) return;
      counts.statuses[tx.status] = (counts.statuses[tx.status] || 0) + 1;
      const tt = tx.transaction_type;
      counts.transactionTypes[tt] = (counts.transactionTypes[tt] || 0) + 1;
      const pt = tx.payment_type || "regular";
      counts.paymentTypes[pt] = (counts.paymentTypes[pt] || 0) + 1;
      const pm = tx.payment_method || "-";
      counts.paymentMethods[pm] = (counts.paymentMethods[pm] || 0) + 1;
      counts.tenants[tx.tenant_id] = (counts.tenants[tx.tenant_id] || 0) + 1;
      const has = Boolean(tx.metadata?.coupon_code);
      if (has) counts.coupons.com++; else counts.coupons.sem++;
    });
    return counts;
  }, [transactions, filters]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginated = filteredTransactions.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleString("pt-BR") : "-");
  const formatCurrency = (v?: number | null) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      approved: "Aprovado",
      pending: "Pendente",
      rejected: "Rejeitado",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: "Pagamento",
      type_change: "Mudança de Tipo",
    };
    return labels[type] || type;
  };

  const getPaymentTypeLabel = (type: string | null | undefined) => {
    const labels: Record<string, string> = {
      regular: "Regular",
      cortesia: "Cortesia",
      testers: "Beta Tester",
      promotion: "Promoção",
    };
    return labels[type || "regular"] || (type || "regular");
  };

  const getMethodLabel = (method: string | null | undefined) => {
    const labels: Record<string, string> = {
      pix: "PIX",
      credit: "Crédito",
      manual: "Manual",
      promotion: "Promoção",
      testers: "Testers",
      cortesia: "Cortesia",
      "-": "-",
    };
    return labels[method || "-"] || (method || "-");
  };

  const handleViewTenant = async (tenantId: string) => {
    try {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("*")
        .eq("id", tenantId)
        .single();
      if (error) throw error;
      setSelectedTenant({
        id: (data as any).id,
        name: (data as any).name,
        slug: (data as any).slug || "",
        email: (data as any).email || null,
        status: (data as any).status || "trial",
        trial_ends_at: (data as any).trial_ends_at || null,
        created_at: (data as any).created_at,
        subscription_status: (data as any).subscription_status || null,
        subscription_plan_id: (data as any).subscription_plan_id || null,
        subscription_type: (data as any).subscription_type || null,
        subscription_payment_method: (data as any).subscription_payment_method || null,
        subscription_start_date: (data as any).subscription_start_date || null,
        subscription_next_billing_date: (data as any).subscription_next_billing_date || null,
        subscription_last_payment_date: (data as any).subscription_last_payment_date || null,
        colors: (data as any).colors || null,
        images: (data as any).images || null,
      });
      setDetailsOpen(true);
    } catch (e: any) {
      toast.error("Falha ao abrir detalhes do cliente: " + (e?.message || ""));
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("subscription_transactions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["subscription-transactions-all"] });
      toast.success("Transação excluída com sucesso!");
      void logCrud({ entitySlug: "entity:subscription_transactions", operationSlug: "delete", screenSlug: "transacoes" });
    },
    onError: (error: any) => toast.error("Erro ao excluir: " + error.message),
  });

  if (!isAdminSaas) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">Acesso restrito</h1>
        <p className="text-muted-foreground">Somente Admin SaaS pode visualizar esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transações de Assinatura</h1>
          <p className="text-muted-foreground">Listagem de transações registradas por cliente</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Lista de Transações</CardTitle>
                <CardDescription>
                  {filteredTransactions.length} {filteredTransactions.length === 1 ? "transação" : "transações"}
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative w-full sm:w-[240px]">
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => { setFilters({ ...filters, searchTerm: e.target.value }); setCurrentPage(1); }}
                    className="pl-2"
                  />
                </div>
                <SubscriptionTransactionFilters
                  filters={filters}
                  onFiltersChange={(f) => { setFilters(f); setCurrentPage(1); }}
                  statuses={["approved", "pending", "rejected", "cancelled"]}
                  transactionTypes={["payment", "type_change"]}
                  paymentTypes={["regular", "cortesia", "testers", "promotion"]}
                  paymentMethods={["pix", "credit", "manual", "promotion", "testers", "cortesia", "-"]}
                  tenants={tenantOptions}
                  counts={facetCounts}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Data Pag./Expiração</TableHead>
                      <TableHead>Tipo de Transação/Tipo de Pagamento</TableHead>
                      <TableHead>Valor/Status</TableHead>
                      <TableHead>Cupom</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">Nenhuma transação encontrada</TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((tx: any) => (
                        <TableRow key={tx.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="font-medium">{getTenantName(tx.tenant_id)}</div>
                              <div className="text-xs text-muted-foreground">{tx.tenant_id}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div>{formatDate(tx.payment_date)}</div>
                              <div className="text-xs text-muted-foreground">Expira: {formatDate(tx.expiration_date)}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline">{getTransactionTypeLabel(tx.transaction_type)}</Badge>
                              <Badge variant="outline">{getPaymentTypeLabel(tx.payment_type)}</Badge>
                              {tx.payment_method && <span className="text-xs text-muted-foreground">{getMethodLabel(tx.payment_method)}</span>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{formatCurrency(tx.amount)}</span>
                              <Badge
                                variant={tx.status === "approved" ? "default" : tx.status === "pending" ? "secondary" : tx.status === "rejected" ? "destructive" : "outline"}
                                className={tx.status === "approved" ? "bg-green-600 hover:bg-green-700 text-white" : ""}
                              >
                                {getStatusLabel(tx.status)}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            {tx.metadata?.coupon_code ? (
                              <div className="text-xs">
                                <div>Code: {tx.metadata.coupon_code}</div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-2">
                              <Button variant="ghost" size="icon" className="icon-chip-green" onClick={() => handleViewTenant(tx.tenant_id)} title="Visualizar cliente">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="icon-chip-blue" onClick={() => setTenantProfile({ id: tx.tenant_id, name: getTenantName(tx.tenant_id) })} title="Abrir raio-x do cliente">
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="icon-chip-green"
                                onClick={() => { setEditingTransaction(tx); setEditOpen(true); }}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="icon"
                                className="icon-chip-red"
                                onClick={() => {
                                  if (window.confirm("Excluir esta transação?")) deleteMutation.mutate(tx.id);
                                }}
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages || 1}
                itemsPerPage={itemsPerPage}
                totalItems={filteredTransactions.length}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
              />
            </>
          )}
        </CardContent>
      </Card>

      <EditSubscriptionTransactionModal
        open={editOpen}
        onOpenChange={setEditOpen}
        transaction={editingTransaction}
        tenantId={editingTransaction?.tenant_id}
        tenantName={getTenantName(editingTransaction?.tenant_id || "")}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["subscription-transactions-all"] });
          setEditOpen(false);
        }}
      />

      {selectedTenant && (
        <TenantDetailsModal
          open={detailsOpen}
          onOpenChange={(open) => setDetailsOpen(open)}
          tenant={selectedTenant}
        />
      )}

      {(typeof tenantProfile !== "undefined") && tenantProfile && (
        <RaioXModal
          open={!!tenantProfile}
          onOpenChange={(open) => { if (!open) setTenantProfile(null); }}
          entityTypeLabel={"CLIENTE SAAS"}
          entityName={tenantProfile.name}
          infoItems={[]}
          showPeriodControls
          initialFrom={startOfDay(subDays(new Date(), 6))}
          initialTo={endOfDay(new Date())}
          contentClassName="p-4"
          renderContent={({ periodRange, setLoading }) => (
            <TenantDetails id={tenantProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
          )}
        />
      )}
    </div>
  );
};

export default SubscriptionTransactions;
